/**
 * 指挥系统左侧航拍建模工具箱（交互对齐画板）
 */
import React, { useEffect, useRef, useState } from 'react';
import { Button, Checkbox, Modal, Popover, Tooltip } from 'antd';
import { PushpinOutlined } from '@ant-design/icons';
import OrthoGlbViewer from './OrthoGlbViewer';
import OrthoMapKindMenu from './orthoToolbox/OrthoMapKindMenu';
import OrthoKindMenu from './orthoToolbox/OrthoKindMenu';
import OrthoModelPoseModal from './orthoToolbox/OrthoModelPoseModal';
import OrthoMapLayerManageModal from './orthoToolbox/OrthoMapLayerManageModal';
import OrthoTaskManageModal from './orthoToolbox/OrthoTaskManageModal';
import useMapLayerTasks from '../hooks/useMapLayerTasks';
import useOrthoTasks from '../hooks/useOrthoTasks';
import useOrthoMapOverlays from '../hooks/useOrthoMapOverlays';
import useOrthoBboxSelect from '../hooks/useOrthoBboxSelect';
import usePanoramaProjects from '../hooks/usePanoramaProjects';
import usePanoramaMapPick from '../hooks/usePanoramaMapPick';
import PanoramaManageModal from './orthoToolbox/PanoramaManageModal';
import PanoramaKindMenu from './orthoToolbox/PanoramaKindMenu';
import PanoramaViewer from './PanoramaViewer';
import { clearPanoramaMapPins, syncPanoramaMapPins } from '../utils/dashboard/panoramaMapMarkers';
import { toMapLngLat } from '../utils/dashboard/basemapCoord';
import './OrthoToolbox.css';

export default function OrthoToolbox({
  mapReady,
  getMap,
  /** 成员列表：点击叠加层名称飞设备时用 */
  members = [],
  getMembers,
  canManage = false,
  canApprove = false,
  canViewLayers = false,
  canMapLayerManage = false,
  canMapLayerView = false,
  canPanoramaManage = false,
  canPanoramaView = false,
  onStartInteract,
  /** 与画板共用：为 true 时禁止「地球空白点击→飞设备」 */
  drawingToolsActiveRef,
  isMobile = false,
  /** 手机框选时收起队员栏，给地图腾空间 */
  onRequestMapSelectSpace
}) {
  const [expanded, setExpanded] = useState(false);
  const [pinned, setPinned] = useState(false);
  const holdPanelOpenRef = useRef(false);
  const boxRef = useRef(null);
  const leaveTimerRef = useRef(null);
  /** 因打开二级菜单而自动打开的固定（手动点图钉则清掉） */
  const autoPinnedBySecondaryRef = useRef(false);
  const pinnedRef = useRef(false);
  const hadSecondaryMenuRef = useRef(false);
  const secondaryCollapseTimerRef = useRef(null);
  const pinForSecondaryMenuRef = useRef(() => {});
  const selectHoldRef = useRef({ selectMode: null, bboxPhase: null, mapLayerBusy: false, mapManageOpen: false, orthoManageOpen: false });
  const onSelectChromeRef = useRef(() => {});

  pinnedRef.current = pinned;
  pinForSecondaryMenuRef.current = () => {
    if (!pinnedRef.current) autoPinnedBySecondaryRef.current = true;
    setPinned(true);
    setExpanded(true);
  };

  const clearSecondaryCollapseTimer = () => {
    if (secondaryCollapseTimerRef.current) {
      clearTimeout(secondaryCollapseTimerRef.current);
      secondaryCollapseTimerRef.current = null;
    }
  };

  // 跨 hook 桥接（框选 ↔ 航拍/叠加）
  const beginBboxSelectRef = useRef(() => {});
  const clearBoxPreviewRef = useRef(() => {});
  const setSelectModeRef = useRef(() => {});
  const beginAoiInteractRef = useRef(() => {});
  const endAoiInteractSoonRef = useRef(() => {});
  const setBlockEarthBlankFlyRef = useRef(() => {});

  const mapLayer = useMapLayerTasks({
    canMapLayerManage,
    canMapLayerView,
    holdPanelOpenRef,
    setExpanded,
    setPinned,
    leaveTimerRef
  });

  const ortho = useOrthoTasks({
    canManage,
    canApprove,
    canViewLayers,
    getMap,
    mapReady,
    onStartInteract,
    holdPanelOpenRef,
    expanded,
    setExpanded,
    setPinned,
    pinForSecondaryMenu: () => pinForSecondaryMenuRef.current(),
    beginBboxSelect: (...args) => beginBboxSelectRef.current(...args),
    clearBoxPreview: (...args) => clearBoxPreviewRef.current(...args),
    setSelectMode: (...args) => setSelectModeRef.current(...args),
    beginAoiInteract: (...args) => beginAoiInteractRef.current(...args),
    endAoiInteractSoon: (...args) => endAoiInteractSoonRef.current(...args),
    setBlockEarthBlankFly: (...args) => setBlockEarthBlankFlyRef.current(...args),
    isMobile
  });

  const overlays = useOrthoMapOverlays({
    getMap,
    getMembers: typeof getMembers === 'function' ? getMembers : () => members,
    onStartInteract,
    expanded,
    setExpanded,
    setPinned,
    pinForSecondaryMenu: () => pinForSecondaryMenuRef.current(),
    mapPublishedLayers: mapLayer.mapPublishedLayers,
    setMapPublishedLayers: mapLayer.setMapPublishedLayers,
    loadMapPublishedLayers: mapLayer.loadMapPublishedLayers,
    beginBboxSelect: (...args) => beginBboxSelectRef.current(...args),
    isMobile
  });

  const panorama = usePanoramaProjects({
    canManage: canPanoramaManage,
    canView: canPanoramaView,
    holdPanelOpenRef,
    setExpanded,
    setPinned,
    pinForSecondaryMenu: () => pinForSecondaryMenuRef.current()
  });

  const [panoMapPickFill, setPanoMapPickFill] = useState(null);
  const panoramaMarkersRef = useRef(new Map());

  const panoMapPick = usePanoramaMapPick({
    getMap,
    drawingToolsActiveRef,
    setBlockEarthBlankFly: (...args) => setBlockEarthBlankFlyRef.current(...args),
    onStartInteract
  });

  const refreshPanoramaMapPins = () => {
    const map = getMap?.();
    if (!map || !mapReady) return;
    if (!canPanoramaView && !canPanoramaManage) {
      clearPanoramaMapPins(panoramaMarkersRef.current);
      return;
    }
    if (!panorama.showMapMarkers) {
      clearPanoramaMapPins(panoramaMarkersRef.current);
      return;
    }
    syncPanoramaMapPins(map, panorama.mapPins, panoramaMarkersRef.current, undefined, {
      onOpen: (pin) => panorama.openViewer(pin.projectId, pin.sceneId),
      size: panorama.markerSize,
      iconUrl: panorama.markerIconUrl
    });
  };

  /** 场景有 WGS84 坐标时，打开前飞到该点 */
  const flyToPanoramaScene = (scene) => {
    const map = getMap?.();
    if (!map || !scene) return false;
    const lon = Number(scene.lon);
    const lat = Number(scene.lat);
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return false;
    try {
      onStartInteract?.();
      const mapped = toMapLngLat(lon, lat, undefined, map) || [lon, lat];
      const z = map.getZoom?.();
      map.easeTo({
        center: mapped,
        zoom: Number.isFinite(z) ? Math.max(z, 15) : 15,
        duration: 1100,
        essential: true
      });
      return true;
    } catch (_) {
      return false;
    }
  };

  useEffect(() => {
    if (!mapReady || (!canPanoramaView && !canPanoramaManage)) return undefined;
    let cancelled = false;
    (async () => {
      await panorama.loadMapSettings();
      if (cancelled) return;
      await panorama.loadMapPins();
    })();
    return () => { cancelled = true; };
  }, [mapReady, canPanoramaView, canPanoramaManage]);

  useEffect(() => {
    refreshPanoramaMapPins();
  }, [
    mapReady,
    panorama.showMapMarkers,
    panorama.markerSize,
    panorama.markerIconUrl,
    panorama.mapPins,
    canPanoramaView,
    canPanoramaManage
  ]);

  useEffect(() => {
    if (!mapReady) return undefined;
    const map = getMap?.();
    if (!map) return undefined;
    const onStyle = () => refreshPanoramaMapPins();
    map.on('style.load', onStyle);
    return () => {
      try { map.off('style.load', onStyle); } catch (_) { /* ignore */ }
    };
  }, [mapReady, panorama.showMapMarkers, panorama.markerSize, panorama.markerIconUrl, panorama.mapPins]);

  useEffect(() => () => {
    clearPanoramaMapPins(panoramaMarkersRef.current);
  }, []);

  const bbox = useOrthoBboxSelect({
    getMap,
    mapReady,
    onStartInteract,
    drawingToolsActiveRef,
    setExpanded,
    setBusy: ortho.setBusy,
    canManage,
    setOrthoManageOpen: ortho.setOrthoManageOpen,
    ensureMapVisibleForSelect: ortho.ensureMapVisibleForSelect,
    prepareFlatMapForPhotoFly: ortho.prepareFlatMapForPhotoFly,
    flyToPhotoRenderArea: ortho.flyToPhotoRenderArea,
    isMobile,
    onSelectChrome: () => onSelectChromeRef.current(),
    activeId: ortho.activeId,
    onConfirm: async ({ mode, wgs }) => {
      if (mode === 'map-road' || mode === 'map-contour' || mode === 'map-poi' || mode === 'map-building') {
        await overlays.confirmMapThemeBbox(mode, wgs);
      } else if (mode === 'ortho-2d' || mode === 'ortho-3d') {
        await ortho.confirmOrthoLoadBbox(mode, wgs);
      } else if (mode === 'render-aoi') {
        await ortho.confirmRenderAoiBbox(wgs);
      }
    }
  });

  onSelectChromeRef.current = () => {
    if (!isMobile) {
      setExpanded(true);
      return;
    }
    const fold = () => {
      setExpanded(false);
      setPinned(false);
      overlays.setMapKindMenuOpen({ road: false, contour: false });
      ortho.setOrthoKindMenuOpen({ '2d': false, '3d': false });
      onRequestMapSelectSpace?.();
    };
    fold();
    queueMicrotask(fold);
  };

  useEffect(() => {
    beginBboxSelectRef.current = bbox.beginBboxSelect;
    clearBoxPreviewRef.current = bbox.clearBoxPreview;
    setSelectModeRef.current = bbox.setSelectMode;
    beginAoiInteractRef.current = bbox.beginAoiInteract;
    endAoiInteractSoonRef.current = bbox.endAoiInteractSoon;
    setBlockEarthBlankFlyRef.current = bbox.setBlockEarthBlankFly;
  });

  useEffect(() => () => {
    if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
    clearSecondaryCollapseTimer();
  }, []);

  const {
    mapLayerTasks,
    setMapLayerKind,
    mapLayerTitle,
    mapLayerDescription,
    setMapLayerTitle,
    setMapLayerDescription,
    contourInterval,
    setContourInterval,
    mapLayerBusy,
    mapPublishedLayers,
    mapLayerUpload,
    setMapLayerUpload,
    mapManageOpen,
    setMapManageOpen,
    mapManageKind,
    setMapManageKind,
    mapLayerFileRef,
    pendingMapLayerTaskIdRef,
    mapLayerTasksRef,
    loadMapLayerTasks,
    loadMapPublishedLayers,
    loadSliceReadyCheck,
    createMapLayerTask,
    uploadMapLayerSource,
    cancelMapLayerTask,
    deleteMapLayerTask,
    startMapLayerSlice,
    requeueMapLayerTask,
    openMapManage,
    pickMapLayerFile
  } = mapLayer;

  const {
    tasks,
    pending,
    activeId,
    setActiveId,
    activeTask,
    title,
    setTitle,
    code,
    setCode,
    wantGlb,
    setWantGlb,
    purgeInboxOnSuccess,
    setPurgeInboxOnSuccess,
    busy,
    layers,
    revealedCode,
    glbViewer,
    setGlbViewer,
    assetsModalOpen,
    setAssetsModalOpen,
    glbMapActive,
    glbLoading,
    orthoKindChecked,
    orthoKindMenuOpen,
    setOrthoKindMenuOpen,
    orthoManageOpen,
    setOrthoManageOpen,
    ortho2dActive,
    refreshStatusBar,
    loadTasks,
    loadPending,
    loadLayers,
    createTask,
    markReady,
    unreadyTask,
    withdrawRequest,
    deleteTask,
    startRenderAoiSelect,
    prepareRemodel,
    clearTaskAssets,
    abortModeling,
    requestStart,
    confirmCode,
    approveTask,
    revealCode,
    rejectTask,
    openGlbViewer,
    attachGlbToLayer,
    poseEditor,
    poseSaving,
    openPoseEditor,
    previewModelPose,
    saveModelPose,
    closePoseEditor,
    flyToOrthoLayer,
    flyToOrtho3d,
    onOrthoKindLabelOpenChange,
    onOrthoKindMasterCheck,
    onOrthoItemCheck,
    openOrthoManage
  } = ortho;

  const {
    mapOverlayActive,
    mapOverlayOpacity,
    mapKindChecked,
    mapAuxChecked,
    mapKindMenuOpen,
    setMapKindMenuOpen,
    thirdPartyPanelOpen,
    setThirdPartyPanelOpen,
    contourDisplayIntervalById,
    onLayerContourIntervalChange,
    onMapAuxCheck,
    onMapKindMasterCheck,
    onMapKindLabelOpenChange,
    onMapKindTreeCheck,
    onPublishedLayerNameClick,
    applyMapOverlayOpacity
  } = overlays;

  const {
    selectMode,
    bboxPhase,
    beginBboxSelect,
    cancelBboxSelect,
    redrawBboxSelect,
    confirmDraftBbox
  } = bbox;

  useEffect(() => {
    if (!expanded) return undefined;
    loadTasks();
    loadPending();
    loadLayers();
    loadMapLayerTasks();
    loadMapPublishedLayers();
    loadSliceReadyCheck();
    refreshStatusBar();
    if (canPanoramaView || canPanoramaManage) {
      panorama.loadProjects({ publishedOnly: !canPanoramaManage });
      panorama.loadMapSettings();
      panorama.loadMapPins();
    }
    const t = setInterval(() => {
      loadTasks();
      loadPending();
      loadLayers();
      loadMapLayerTasks();
      loadMapPublishedLayers();
      refreshStatusBar();
      if (canPanoramaView || canPanoramaManage) {
        panorama.loadMapPins();
      }
    }, 15000);
    return () => clearInterval(t);
  }, [
    expanded,
    loadTasks,
    loadPending,
    loadLayers,
    loadMapLayerTasks,
    loadMapPublishedLayers,
    loadSliceReadyCheck,
    refreshStatusBar,
    canPanoramaView,
    canPanoramaManage,
    panorama.loadProjects
  ]);

  const openPanel = () => {
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
    clearSecondaryCollapseTimer();
    setExpanded(true);
  };

  const scheduleClose = () => {
    if (pinned || holdPanelOpenRef.current || mapLayerBusy
      || mapManageOpen || orthoManageOpen || panorama.manageOpen || panorama.menuOpen
      || mapKindMenuOpen.road || mapKindMenuOpen.contour
      || orthoKindMenuOpen['2d'] || orthoKindMenuOpen['3d']
      || panoMapPick.isPicking) return;
    // 桌面框选时保持展开；手机框选已收起工具箱，允许保持收起
    if (!isMobile && (selectMode || bboxPhase)) return;
    if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
    leaveTimerRef.current = setTimeout(() => setExpanded(false), 480);
  };

  const togglePinned = () => {
    setPinned((v) => {
      const next = !v;
      // 手动切换图钉：不再视为「二级菜单自动固定」
      autoPinnedBySecondaryRef.current = false;
      clearSecondaryCollapseTimer();
      if (next) setExpanded(true);
      return next;
    });
  };

  // 供 5 秒收起定时器读取的瞬时占位状态（避免闭包过期）
  selectHoldRef.current = {
    selectMode,
    bboxPhase,
    mapLayerBusy,
    mapManageOpen,
    orthoManageOpen,
    panoramaManageOpen: panorama.manageOpen,
    panoramaMenuOpen: panorama.menuOpen,
    holdPanel: holdPanelOpenRef.current
  };

  // 二级菜单全部关闭后：取消自动固定，并在 5 秒后缓慢收起
  useEffect(() => {
    const anySecondaryOpen = !!(
      mapKindMenuOpen.road
      || mapKindMenuOpen.contour
      || orthoKindMenuOpen['2d']
      || orthoKindMenuOpen['3d']
      || panorama.menuOpen
    );

    if (anySecondaryOpen) {
      hadSecondaryMenuRef.current = true;
      clearSecondaryCollapseTimer();
      return undefined;
    }

    if (!hadSecondaryMenuRef.current) return undefined;
    hadSecondaryMenuRef.current = false;

    if (autoPinnedBySecondaryRef.current) {
      autoPinnedBySecondaryRef.current = false;
      setPinned(false);
    }

    clearSecondaryCollapseTimer();
    secondaryCollapseTimerRef.current = setTimeout(() => {
      secondaryCollapseTimerRef.current = null;
      const hold = selectHoldRef.current || {};
      if (pinnedRef.current) return;
      if (hold.selectMode || hold.bboxPhase || hold.mapLayerBusy
        || hold.mapManageOpen || hold.orthoManageOpen
        || hold.panoramaManageOpen || hold.panoramaMenuOpen
        || hold.holdPanel) return;
      // 指针仍停在一级菜单上：交由 mouseleave → scheduleClose，不强收
      if (boxRef.current?.matches?.(':hover')) return;
      setExpanded(false);
    }, 5000);

    // 不在 effect cleanup 里清定时器（StrictMode 会误杀）；重开二级菜单 / 卸载时再清
    return undefined;
  }, [
    mapKindMenuOpen.road,
    mapKindMenuOpen.contour,
    orthoKindMenuOpen['2d'],
    orthoKindMenuOpen['3d'],
    panorama.menuOpen
  ]);

  if (!canManage && !canApprove && !canViewLayers && !canMapLayerManage && !canMapLayerView
    && !canPanoramaManage && !canPanoramaView) return null;

  const bboxModeLabel = selectMode === 'map-road'
    ? '系统路网'
    : selectMode === 'map-contour'
      ? '系统等高线'
      : selectMode === 'map-poi'
        ? '地标'
        : selectMode === 'map-building'
          ? '建筑'
          : selectMode === 'ortho-2d'
            ? '2D航拍'
            : selectMode === 'ortho-3d'
              ? '3D建模'
              : '渲染区';

  const renderOrthoKindMenu = (kind) => (
    <OrthoKindMenu
      kind={kind}
      layers={layers}
      canManage={canManage}
      busy={busy}
      glbMapActive={glbMapActive}
      glbLoading={glbLoading}
      ortho2dActive={ortho2dActive}
      onOpenManage={openOrthoManage}
      onBeginBboxSelect={beginBboxSelect}
      onCloseMenu={(k) => setOrthoKindMenuOpen((prev) => ({ ...prev, [k]: false }))}
      onItemCheck={onOrthoItemCheck}
      onFlyTo2d={flyToOrthoLayer}
      onFlyTo3d={flyToOrtho3d}
      onOpenGlbViewer={openGlbViewer}
      onAttachGlb={attachGlbToLayer}
      onOpenPoseEditor={openPoseEditor}
    />
  );

  const renderMapKindMenu = (kind) => (
    <OrthoMapKindMenu
      kind={kind}
      canMapLayerManage={canMapLayerManage}
      mapPublishedLayers={mapPublishedLayers}
      mapOverlayActive={mapOverlayActive}
      mapAuxChecked={mapAuxChecked}
      mapOverlayOpacity={mapOverlayOpacity}
      thirdPartyPanelOpen={thirdPartyPanelOpen}
      contourDisplayIntervalById={contourDisplayIntervalById}
      onLayerContourIntervalChange={onLayerContourIntervalChange}
      onOpenManage={openMapManage}
      onBeginBboxSelect={beginBboxSelect}
      onCloseMenu={(k) => setMapKindMenuOpen((prev) => ({ ...prev, [k]: false }))}
      onTreeCheck={onMapKindTreeCheck}
      onLayerNameClick={onPublishedLayerNameClick}
      onAuxCheck={onMapAuxCheck}
      onOpacityChange={applyMapOverlayOpacity}
      onThirdPartyOpenChange={(k, checked) => setThirdPartyPanelOpen((prev) => ({ ...prev, [k]: checked }))}
    />
  );

  return (
    <>
    {selectMode && (
      <div className={`zhxt-ortho-bbox-bar${isMobile ? ' is-mobile' : ''}`}>
        <span className="zhxt-ortho-bbox-bar-text">
          {isMobile
            ? (bboxPhase === 'editing'
              ? `${bboxModeLabel}：拖白点调大小，点确认生效`
              : `${bboxModeLabel}：手指拖动画框`)
            : (bboxPhase === 'editing'
              ? `${bboxModeLabel}选框：拖白点调大小；滚轮可缩放；右键取消；确认后生效`
              : `${bboxModeLabel}框选中（十字光标）：拖拽画框；滚轮缩放不退出；右键取消`)}
        </span>
        <div className="zhxt-ortho-bbox-actions">
          {bboxPhase === 'editing' && (
            <Button size="small" type="primary" loading={busy} onClick={confirmDraftBbox}>确认选区</Button>
          )}
          <Button size="small" onClick={redrawBboxSelect}>重画</Button>
          <Button size="small" onClick={cancelBboxSelect}>取消</Button>
        </div>
      </div>
    )}
    {panoMapPick.isPicking && (
      <div className={`zhxt-ortho-bbox-bar${isMobile ? ' is-mobile' : ''}`}>
        <span className="zhxt-ortho-bbox-bar-text">
          {panoMapPick.pickSession?.pendingWgs
            ? `已选点 ${Number(panoMapPick.pickSession.pendingWgs[0]).toFixed(6)}, ${Number(panoMapPick.pickSession.pendingWgs[1]).toFixed(6)}（可再点调整）；确认后回填管理窗`
            : `全景挂点选点中${panoMapPick.pickSession?.sceneName ? `「${panoMapPick.pickSession.sceneName}」` : ''}：请在地图上点击`}
        </span>
        <div className="zhxt-ortho-bbox-actions">
          <Button
            size="small"
            type="primary"
            onClick={() => {
              const result = panoMapPick.confirmPick();
              if (!result) return;
              setPanoMapPickFill(result);
              panorama.setManageOpen(true);
              setPinned(true);
              setExpanded(true);
            }}
          >
            确认选点
          </Button>
          <Button
            size="small"
            onClick={() => {
              panoMapPick.cancelPick();
              panorama.setManageOpen(true);
              setPinned(true);
              setExpanded(true);
            }}
          >
            取消
          </Button>
        </div>
      </div>
    )}
    <div
      ref={boxRef}
      className={`zhxt-ortho-box ${expanded || pinned || (!isMobile && selectMode) ? 'expanded' : 'collapsed'} ${pinned ? 'pinned' : ''}`}
      onMouseLeave={scheduleClose}
    >
      <button
        type="button"
        className="zhxt-ortho-tab"
        title="地图叠加（悬停或点击展开）"
        onMouseEnter={openPanel}
        onClick={openPanel}
      >
        <span className="zhxt-ortho-tab-label">地图叠加</span>
      </button>

      {/* 面板常驻 DOM，用 CSS 显隐，避免挂载/卸载造成悬停闪烁 */}
      <div className="zhxt-ortho-panel" onMouseEnter={openPanel}>
          <div className="zhxt-ortho-header">
            <Tooltip
              placement="right"
              title="路网/等高线大文件直传 OSS。第三方数据源入口在对应二级菜单内（待正式 API）。建模需管理员确认或页面验证码。"
            >
              <h4 className="zhxt-ortho-header-title">地图叠加工具箱</h4>
            </Tooltip>
            <Tooltip title={pinned ? '取消固定' : '固定菜单'}>
              <Button
                size="small"
                type={pinned ? 'primary' : 'default'}
                icon={<PushpinOutlined />}
                onClick={togglePinned}
              />
            </Tooltip>
          </div>
          <div className="zhxt-ortho-panel-body">

          {(canMapLayerView || canMapLayerManage) && (
            <div className="zhxt-ortho-section">
              <Tooltip
                placement="right"
                title="点「路网/等高线」文字只开菜单、不改勾选；点复选框勾选会弹出竖型菜单；取消勾选时，若已叠加两项及以上会询问是否全部关闭。"
              >
                <div className="zhxt-ortho-section-title" style={{ cursor: 'help' }}>路网 / 等高线</div>
              </Tooltip>
              <div className="zhxt-map-kind-row">
                <div className="zhxt-map-kind-check">
                  <Checkbox
                    checked={!!mapKindChecked.road}
                    onChange={(e) => onMapKindMasterCheck('road', e.target.checked)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <Popover
                    trigger="click"
                    open={!!mapKindMenuOpen.road}
                    onOpenChange={(open) => onMapKindLabelOpenChange('road', open)}
                    content={renderMapKindMenu('road')}
                    placement="rightTop"
                    overlayClassName="zhxt-map-kind-popover"
                  >
                    <button
                      type="button"
                      className="zhxt-map-kind-label"
                      onClick={(e) => e.stopPropagation()}
                    >
                      路网
                    </button>
                  </Popover>
                </div>
                <div className="zhxt-map-kind-check">
                  <Checkbox
                    checked={!!mapKindChecked.contour}
                    onChange={(e) => onMapKindMasterCheck('contour', e.target.checked)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <Popover
                    trigger="click"
                    open={!!mapKindMenuOpen.contour}
                    onOpenChange={(open) => onMapKindLabelOpenChange('contour', open)}
                    content={renderMapKindMenu('contour')}
                    placement="rightTop"
                    overlayClassName="zhxt-map-kind-popover"
                  >
                    <button
                      type="button"
                      className="zhxt-map-kind-label"
                      onClick={(e) => e.stopPropagation()}
                    >
                      等高线
                    </button>
                  </Popover>
                </div>
              </div>

              {canMapLayerManage && (
                <OrthoMapLayerManageModal
                  open={mapManageOpen}
                  mapManageKind={mapManageKind}
                  mapLayerTitle={mapLayerTitle}
                  mapLayerDescription={mapLayerDescription}
                  contourInterval={contourInterval}
                  mapLayerBusy={mapLayerBusy}
                  mapLayerUpload={mapLayerUpload}
                  mapLayerTasks={mapLayerTasks}
                  mapLayerFileRef={mapLayerFileRef}
                  onClose={() => setMapManageOpen(false)}
                  onManageKindChange={(v) => { setMapManageKind(v); setMapLayerKind(v); }}
                  onTitleChange={setMapLayerTitle}
                  onDescriptionChange={setMapLayerDescription}
                  onContourIntervalChange={setContourInterval}
                  onCreateTask={createMapLayerTask}
                  onClearUpload={() => setMapLayerUpload(null)}
                  onFileInputChange={(e) => {
                  const file = e.target.files?.[0];
                  const taskId = Number(
                    pendingMapLayerTaskIdRef.current
                    || e.target.dataset.taskId
                    || mapLayerUpload?.taskId
                  );
                  if (!file) {
                    pendingMapLayerTaskIdRef.current = null;
                    setMapLayerUpload((prev) => (
                      prev?.phase === 'picking'
                        ? { ...prev, phase: 'error', error: '未选择文件（已取消）' }
                        : prev
                    ));
                    holdPanelOpenRef.current = false;
                    return;
                  }
                  if (!Number.isFinite(taskId) || taskId <= 0) {
                    setMapLayerUpload({
                      taskId: taskId || 0,
                      fileName: file.name,
                      fileSize: file.size,
                      phase: 'error',
                      percent: 0,
                      error: '未绑定任务编号，请关闭提示后重新点「上传」',
                      done: false
                    });
                    return;
                  }
                  const task = (mapLayerTasksRef.current || []).find((t) => Number(t.id) === taskId)
                    || { id: taskId };
                  pendingMapLayerTaskIdRef.current = null;
                  uploadMapLayerSource(task, file);
                }}
                  onPickFile={pickMapLayerFile}
                  onStartSlice={startMapLayerSlice}
                  onRequeue={requeueMapLayerTask}
                  onCancelTask={cancelMapLayerTask}
                  onDeleteTask={deleteMapLayerTask}
                />
              )}
            </div>
          )}

          {(canViewLayers || canManage) && (
            <div className="zhxt-ortho-section zhxt-ortho-section--aerial">
              <Tooltip
                placement="right"
                title="点「2D航拍/3D建模」文字只开菜单不改勾选；勾选一级只弹出菜单，须点子项复选框才叠加。取消一级：仅 1 条时直接关，≥2 条才确认。管理弹窗内可新建任务、上传航片、框选渲染区。"
              >
                <div className="zhxt-ortho-section-title" style={{ cursor: 'help' }}>2D航拍 / 3D建模</div>
              </Tooltip>
              <div className="zhxt-map-kind-row">
                <div className="zhxt-map-kind-check">
                  <Checkbox
                    checked={!!orthoKindChecked['2d']}
                    onChange={(e) => onOrthoKindMasterCheck('2d', e.target.checked)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <Popover
                    trigger="click"
                    open={!!orthoKindMenuOpen['2d']}
                    onOpenChange={(open) => onOrthoKindLabelOpenChange('2d', open)}
                    content={renderOrthoKindMenu('2d')}
                    placement="rightTop"
                    overlayClassName="zhxt-map-kind-popover"
                  >
                    <button
                      type="button"
                      className="zhxt-map-kind-label"
                      onClick={(e) => e.stopPropagation()}
                    >
                      2D航拍
                    </button>
                  </Popover>
                </div>
                <div className="zhxt-map-kind-check">
                  <Checkbox
                    checked={!!orthoKindChecked['3d']}
                    onChange={(e) => onOrthoKindMasterCheck('3d', e.target.checked)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <Popover
                    trigger="click"
                    open={!!orthoKindMenuOpen['3d']}
                    onOpenChange={(open) => onOrthoKindLabelOpenChange('3d', open)}
                    content={renderOrthoKindMenu('3d')}
                    placement="rightTop"
                    overlayClassName="zhxt-map-kind-popover"
                  >
                    <button
                      type="button"
                      className="zhxt-map-kind-label"
                      onClick={(e) => e.stopPropagation()}
                    >
                      3D建模
                    </button>
                  </Popover>
                </div>
              </div>

              {canManage && (
                <OrthoTaskManageModal
                  open={orthoManageOpen}
                  title={title}
                  tasks={tasks}
                  activeId={activeId}
                  activeTask={activeTask}
                  busy={busy}
                  code={code}
                  wantGlb={wantGlb}
                  purgeInboxOnSuccess={purgeInboxOnSuccess}
                  assetsModalOpen={assetsModalOpen}
                  holdPanelOpenRef={holdPanelOpenRef}
                  onClose={() => setOrthoManageOpen(false)}
                  onTitleChange={setTitle}
                  onCreateTask={createTask}
                  onSelectTask={setActiveId}
                  onDeleteTask={deleteTask}
                  onCodeChange={setCode}
                  onConfirmCode={confirmCode}
                  onAbortModeling={abortModeling}
                  onOpenAssets={() => { setPinned(true); setAssetsModalOpen(true); }}
                  onCloseAssets={() => setAssetsModalOpen(false)}
                  onAssetsChanged={() => { loadTasks(); refreshStatusBar(); }}
                  onStartRenderAoi={startRenderAoiSelect}
                  onRequestStart={requestStart}
                  onWantGlbChange={setWantGlb}
                  onPurgeChange={setPurgeInboxOnSuccess}
                  onMarkReady={() => markReady()}
                  onUnready={() => unreadyTask()}
                  onWithdraw={() => withdrawRequest()}
                  onPrepareRemodel={() => prepareRemodel()}
                  onClearAssets={() => clearTaskAssets()}
                />
              )}
            </div>
          )}

          {(canPanoramaView || canPanoramaManage) && (
            <div className="zhxt-ortho-section">
              <Popover
                trigger="click"
                open={!!panorama.menuOpen}
                onOpenChange={(open) => panorama.onMenuOpenChange(open)}
                content={(
                  <PanoramaKindMenu
                    projects={(panorama.projects || []).filter(
                      (p) => canPanoramaManage || p.status === 'published'
                    )}
                    canManage={canPanoramaManage}
                    busy={panorama.busy}
                    onOpenManage={() => {
                      panorama.closeMenu();
                      panorama.openManage();
                    }}
                    onOpenProject={(p) => {
                      // 打开项目：优先飞到封面场景，否则第一个有坐标的场景
                      const scenes = p.scenes || [];
                      const coverId = p.coverSceneId;
                      const cover = coverId != null
                        ? scenes.find((s) => Number(s.id) === Number(coverId))
                        : null;
                      const withCoord = cover?.lon != null && cover?.lat != null
                        ? cover
                        : scenes.find((s) => Number.isFinite(Number(s.lon)) && Number.isFinite(Number(s.lat)));
                      if (withCoord) flyToPanoramaScene(withCoord);
                      panorama.openViewer(p.id);
                      panorama.closeMenu();
                    }}
                    onOpenScene={(p, s) => {
                      flyToPanoramaScene(s);
                      panorama.openViewer(p.id, s.id);
                      panorama.closeMenu();
                    }}
                  />
                )}
                placement="rightTop"
                overlayClassName="zhxt-map-kind-popover zhxt-panorama-kind-popover"
              >
                <button
                  type="button"
                  className="zhxt-ortho-section-title zhxt-map-kind-label"
                  style={{ cursor: 'pointer', textAlign: 'left', width: '100%' }}
                  title="点击打开全景二级菜单"
                  onClick={(e) => e.stopPropagation()}
                >
                  全景影像
                </button>
              </Popover>
              {canPanoramaManage && (
                <PanoramaManageModal
                  open={panorama.manageOpen && !panoMapPick.isPicking}
                  busy={panorama.busy}
                  projects={panorama.projects}
                  onClose={panorama.closeManage}
                  onCreateProject={panorama.createProject}
                  onPatchProject={panorama.patchProject}
                  onLoadBundle={panorama.loadBundle}
                  onCreateScene={panorama.createScene}
                  onUploadEquirect={panorama.uploadEquirect}
                  onPatchScene={panorama.patchScene}
                  onDeleteScene={panorama.deleteScene}
                  onCreateHotspot={panorama.createHotspot}
                  onDeleteHotspot={panorama.deleteHotspot}
                  onRefresh={() => panorama.loadProjects({ publishedOnly: false, withScenes: true })}
                  mapPickFill={panoMapPickFill}
                  onConsumeMapPickFill={() => setPanoMapPickFill(null)}
                  onBeginMapPick={({ sceneId, sceneName, projectId }) => {
                    holdPanelOpenRef.current = true;
                    panorama.setManageOpen(false);
                    const ok = panoMapPick.beginPick({ sceneId, sceneName });
                    if (!ok) {
                      panorama.setManageOpen(true);
                      return;
                    }
                    if (isMobile) {
                      setExpanded(false);
                      setPinned(false);
                      onRequestMapSelectSpace?.();
                    } else {
                      setExpanded(true);
                      setPinned(true);
                    }
                    void projectId;
                  }}
                />
              )}
              <PanoramaViewer
                open={!!panorama.viewer}
                projectId={panorama.viewer?.projectId}
                initialSceneId={panorama.viewer?.sceneId}
                onClose={panorama.closeViewer}
                loadBundle={panorama.loadBundle}
              />
            </div>
          )}

          {selectMode && !isMobile && (
            <div className="zhxt-ortho-section">
              <div className="zhxt-ortho-select-hint">
                {bboxPhase === 'editing'
                  ? `已画${bboxModeLabel}选框：拖边角/拖整体调整，确认后生效`
                  : `正在框选${bboxModeLabel}… 拖拽画半透明框；滚轮可缩放`}
                <div className="zhxt-ortho-bbox-actions">
                  {bboxPhase === 'editing' && (
                    <Button size="small" type="primary" loading={busy} onClick={confirmDraftBbox}>确认选区</Button>
                  )}
                  <Button size="small" onClick={redrawBboxSelect}>重画</Button>
                  <Button size="small" type="link" onClick={cancelBboxSelect}>取消</Button>
                </div>
              </div>
            </div>
          )}

          {canApprove && (
            <div className="zhxt-ortho-section">
              <div style={{ fontWeight: 600 }}>待我审批</div>
              {revealedCode && (
                <div className="zhxt-ortho-select-hint">当前验证码：<b>{revealedCode}</b>（请口头/微信告知申请人）</div>
              )}
              {(pending || []).length === 0 && (
                <div className="zhxt-ortho-muted" style={{ marginTop: 6 }}>暂无待审批任务</div>
              )}
              {(pending || []).map((t) => (
                <div key={t.id} className="zhxt-ortho-task" style={{ marginTop: 6 }}>
                  <div className="zhxt-ortho-task-title">#{t.id} {t.title}</div>
                  <div className="zhxt-ortho-actions">
                    <Button size="small" type="primary" loading={busy} onClick={() => approveTask(t.id)}>确认启动</Button>
                    <Button size="small" onClick={() => revealCode(t.id)}>查看验证码</Button>
                    <Button size="small" danger onClick={() => rejectTask(t.id)}>驳回</Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* 无审批权时保留底部分隔线与留白，避免面板在「全景影像」处收得过陡 */}
          {!canApprove && (
            <div className="zhxt-ortho-section zhxt-ortho-section-tail" aria-hidden="true" />
          )}

          <OrthoModelPoseModal
            open={!!poseEditor}
            layer={poseEditor}
            saving={poseSaving}
            mapActive={!!(poseEditor && glbMapActive[poseEditor.id])}
            onCancel={closePoseEditor}
            onPreview={previewModelPose}
            onSave={saveModelPose}
          />

          <Modal
            open={!!glbViewer}
            title={(
              <Tooltip
                title={(
                  <span>
                    左键拖＝平移；右键或 Ctrl+左键拖＝旋转；Shift+左键拖＝框选放大；滚轮＝缩放。
                  </span>
                )}
              >
                <span style={{ cursor: 'help' }}>{glbViewer ? `3D 模型 · ${glbViewer.name}` : '3D 模型'}</span>
              </Tooltip>
            )}
            onCancel={() => setGlbViewer(null)}
            footer={null}
            width={800}
            destroyOnClose
            styles={{ body: { paddingTop: 12 } }}
          >
            {glbViewer?.url && (
              <div
                onMouseDown={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                style={{ touchAction: 'none', userSelect: 'none' }}
              >
                <OrthoGlbViewer url={glbViewer.url} height={520} />
              </div>
            )}
          </Modal>

          </div>
      </div>
    </div>
    </>
  );
}

